// src/pages/OvertimeOverview.js
import React, { useEffect, useMemo, useState } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { FaSearch, FaBusinessTime, FaRegClock, FaMoneyBillWave, FaUser, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaCheck, FaTimes, FaUndo, FaBriefcase } from "react-icons/fa";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  getDoc,
  getDocs,
  orderBy,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";

// ==================== HJÄLPFUNKTIONER ====================

// Säkert tolka datumvärden
function safeParseDate(dateValue) {
  if (!dateValue) return null;
  try {
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue?.toDate === "function") return dateValue.toDate();
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? null : parsed;
  } catch (error) {
    return null;
  }
}

// Formatera datum för visning
function formatDate(dateValue) {
  const date = safeParseDate(dateValue);
  if (!date) return "N/A";
  return date.toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatDateTime(dateValue) {
  const date = safeParseDate(dateValue);
  if (!date) return "N/A";
  return date.toLocaleString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTime(timeString) {
  if (!timeString) return "N/A";
  return timeString.substring(0, 5);
}

// Status configuration
const getStatusConfig = (status) => {
  switch (status) {
    case "pending":
      return { text: "Väntar", color: "#F59E0B", bg: "#FEF3C7", icon: FaHourglassHalf };
    case "approved":
      return { text: "Godkänd", color: "#10B981", bg: "#D1FAE5", icon: FaCheckCircle };
    case "rejected":
      return { text: "Nekad", color: "#EF4444", bg: "#FEE2E2", icon: FaTimesCircle };
    default:
      return { text: "Okänd", color: "#6B7280", bg: "#F3F4F6", icon: FaRegClock };
  }
};

// Formatera timmar
const formatHours = (hours) => {
  return Number(hours || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

// Hämta anställds fullständiga namn
const getEmployeeFullName = (employee) => {
  if (!employee) return null;
  
  if (employee.firstName && employee.lastName) {
    return `${employee.firstName} ${employee.lastName}`;
  }
  if (employee.firstName) {
    return employee.firstName;
  }
  if (employee.lastName) {
    return employee.lastName;
  }
  if (employee.name) {
    return employee.name;
  }
  if (employee.displayName) {
    return employee.displayName;
  }
  return null;
};

// ==================== MODAL KOMPONENT ====================

function ReviewModal({ application, onClose, onApprove, onReject, onRevoke, isProcessing, actionType }) {
  const [comment, setComment] = useState(application?.reviewComment || "");
  
  if (!application) return null;
  
  const isApproved = application.status === "approved";
  const isRejected = application.status === "rejected";

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={modalStyles.title}>
            {actionType === "approve" && "Godkänn övertidsansökan"}
            {actionType === "reject" && "Neka övertidsansökan"}
            {actionType === "revoke" && (isApproved ? "Återkalla godkänd ansökan" : "Återkalla nekad ansökan")}
          </h3>
          <button style={modalStyles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.infoRow}>
            <span style={modalStyles.label}>📅 Datum:</span>
            <span>{formatDate(application.date)}</span>
          </div>
          <div style={modalStyles.infoRow}>
            <span style={modalStyles.label}>⏰ Tid:</span>
            <span>{formatTime(application.startTime)} - {formatTime(application.endTime)}</span>
          </div>
          
          {application.shiftTitle && (
            <div style={modalStyles.infoRow}>
              <span style={modalStyles.label}>📋 Pass:</span>
              <span>{application.shiftTitle}</span>
            </div>
          )}

          {application.shiftStartTime && application.shiftEndTime && (
            <div style={modalStyles.infoRow}>
              <span style={modalStyles.label}>⏱️ Ordinarie tid:</span>
              <span>{formatTime(application.shiftStartTime)} - {formatTime(application.shiftEndTime)}</span>
            </div>
          )}

          <div style={modalStyles.infoRow}>
            <span style={modalStyles.label}>⏱️ Övertidstimmar:</span>
            <span>{formatHours(application.hours)} h</span>
          </div>
          <div style={modalStyles.infoRow}>
            <span style={modalStyles.label}>📨 Insänd:</span>
            <span>{formatDateTime(application.submittedAt)}</span>
          </div>
          {application.reason && (
            <div style={modalStyles.infoRow}>
              <span style={modalStyles.label}>💬 Anledning:</span>
              <span>{application.reason}</span>
            </div>
          )}
          {application.reviewComment && (
            <div style={modalStyles.infoRow}>
              <span style={modalStyles.label}>💭 Tidigare kommentar:</span>
              <span>{application.reviewComment}</span>
            </div>
          )}

          {(actionType === "approve" || actionType === "reject") && (
            <div style={modalStyles.commentSection}>
              <label style={modalStyles.commentLabel}>
                Kommentar (valfritt):
              </label>
              <textarea
                style={modalStyles.textarea}
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Lägg till en kommentar..."
              />
            </div>
          )}
        </div>

        <div style={modalStyles.footer}>
          {actionType === "approve" && (
            <button
              style={{ ...modalStyles.btn, ...modalStyles.btnApprove }}
              onClick={() => onApprove(application, comment)}
              disabled={isProcessing}
            >
              <FaCheck size={14} /> Godkänn
            </button>
          )}
          {actionType === "reject" && (
            <button
              style={{ ...modalStyles.btn, ...modalStyles.btnReject }}
              onClick={() => onReject(application, comment)}
              disabled={isProcessing}
            >
              <FaTimes size={14} /> Neka
            </button>
          )}
          {actionType === "revoke" && (
            <button
              style={{ ...modalStyles.btn, ...modalStyles.btnRevoke }}
              onClick={() => onRevoke(application)}
              disabled={isProcessing}
            >
              <FaUndo size={14} /> Återkalla
            </button>
          )}
          <button
            style={{ ...modalStyles.btn, ...modalStyles.btnCancel }}
            onClick={onClose}
          >
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "450px",
    maxHeight: "80vh",
    overflow: "auto",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e5e7eb",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#9ca3af",
  },
  body: {
    padding: "20px",
  },
  infoRow: {
    marginBottom: "12px",
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  label: {
    fontWeight: 600,
    minWidth: "110px",
    color: "#374151",
  },
  commentSection: {
    marginTop: "16px",
  },
  commentLabel: {
    display: "block",
    fontSize: "14px",
    fontWeight: 500,
    marginBottom: "8px",
    color: "#374151",
  },
  textarea: {
    width: "100%",
    padding: "10px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    resize: "vertical",
  },
  footer: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    padding: "16px 20px",
    borderTop: "1px solid #e5e7eb",
  },
  btn: {
    padding: "8px 16px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 500,
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  btnApprove: {
    backgroundColor: "#10b981",
    color: "#fff",
  },
  btnReject: {
    backgroundColor: "#ef4444",
    color: "#fff",
  },
  btnRevoke: {
    backgroundColor: "#f59e0b",
    color: "#fff",
  },
  btnCancel: {
    backgroundColor: "#6b7280",
    color: "#fff",
  },
};

// ==================== HUVUDKOMPONENT ====================

export default function OvertimeOverview() {
  const { currentUser } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();

  // State för data
  const [applications, setApplications] = useState([]);
  const [employees, setEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  
  // State för att hålla koll på vilka shiftId som finns
  const [existingShiftIds, setExistingShiftIds] = useState(new Set());
  
  // State för filtrering och sökning
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("pending");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  
  // State för modal
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [modalAction, setModalAction] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Hämta companyAccountUid från activeCompany
  const companyAccountUid = activeCompany?.companyAccountUid;

  // Sätt aktuell månad och år
  useEffect(() => {
    const currentDate = new Date();
    const months = ["Januari", "Februari", "Mars", "April", "Maj", "Juni", "Juli", "Augusti", "September", "Oktober", "November", "December"];
    setSelectedMonth(months[currentDate.getMonth()]);
    setSelectedYear(currentDate.getFullYear().toString());
  }, []);

  // Listor för månader och år
  const monthsInSwedish = [
    "Januari", "Februari", "Mars", "April", "Maj", "Juni",
    "Juli", "Augusti", "September", "Oktober", "November", "December"
  ];

  const generateYears = () => {
    const currentYearNum = new Date().getFullYear();
    const years = [];
    for (let year = 2020; year <= currentYearNum + 2; year++) {
      years.push(year.toString());
    }
    return years;
  };

  const years = generateYears();

  // HÄMTA ALLA EXISTERANDE SHIFT-ID FÖR ATT KUNNA FILTRERA BORT BORTTAGNA PASS
  useEffect(() => {
    if (!companyAccountUid) {
      setExistingShiftIds(new Set());
      return;
    }

    const shiftsRef = collection(db, "users", companyAccountUid, "shifts");
    
    const unsubscribe = onSnapshot(shiftsRef, (snapshot) => {
      const shiftIds = new Set();
      snapshot.forEach((doc) => {
        shiftIds.add(doc.id);
      });
      setExistingShiftIds(shiftIds);
    }, (error) => {
      console.error("Fel vid hämtning av shift-ID:", error);
    });

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Hämta alla anställda baserat på aktivt företag
  useEffect(() => {
    if (!companyAccountUid) {
      setEmployees({});
      return;
    }

    console.log("Hämtar anställda för företag:", activeCompany?.companyName);

    const employedRef = collection(db, "users", companyAccountUid, "employed");

    const unsubscribe = onSnapshot(employedRef, (snapshot) => {
      const usersMap = {};
      snapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        const email = userData.email || "";
        
        usersMap[email] = {
          id: userDoc.id,
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          name: userData.name || "",
          displayName: userData.displayName || "",
          email: email,
        };
      });
      setEmployees(usersMap);
    }, (error) => {
      console.error("Fel vid hämtning av anställda:", error);
    });

    return () => unsubscribe();
  }, [companyAccountUid, activeCompany?.companyName]);

  // Hämta ALLA övertidsansökningar baserat på aktivt företag
  useEffect(() => {
    if (!companyAccountUid) {
      setApplications([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    console.log("Hämtar övertidsansökningar för företag:", activeCompany?.companyName);

    const overtimeRef = collection(db, "users", companyAccountUid, "overtimeRequests");
    const q = query(overtimeRef, orderBy("submittedAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const apps = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        apps.push({
          id: doc.id,
          ...data,
          date: safeParseDate(data.date),
          submittedAt: safeParseDate(data.submittedAt),
          reviewedAt: safeParseDate(data.reviewedAt),
        });
      });
      setApplications(apps);
      setLoading(false);
    }, (error) => {
      console.error("Fel vid hämtning av övertidsansökningar:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyAccountUid, activeCompany?.companyName]);

  // Skapa map för anställdas namn
  const employeeNameMap = useMemo(() => {
    const map = {};
    Object.keys(employees).forEach(email => {
      const employee = employees[email];
      if (employee) {
        const fullName = getEmployeeFullName(employee);
        if (fullName) {
          map[email] = fullName;
        }
      }
    });
    return map;
  }, [employees]);

  // Skapa map för email till employeeId
  const emailToEmployeeIdMap = useMemo(() => {
    const map = {};
    Object.keys(employees).forEach(email => {
      const employee = employees[email];
      if (employee && employee.id) {
        map[email] = employee.id;
      }
    });
    return map;
  }, [employees]);

  // Berika ansökningar med anställdas namn och employeeId
  // OCH FILTRERA BORT ANSÖKNINGAR DÄR PASSET INTE LÄNGRE FINNS
  const enrichedApplications = useMemo(() => {
    return applications
      .map(app => {
        const userEmail = app.userEmail || "";
        const employeeName = employeeNameMap[userEmail] || "Okänd anställd";
        const employeeId = emailToEmployeeIdMap[userEmail] || null;
        return {
          ...app,
          employeeName: employeeName,
          employeeId: employeeId,
        };
      })
      // FILTRERA BORT ANSÖKNINGAR DÄR PASSET INTE LÄNGRE FINNS
      .filter(app => {
        // Om ansökan inte har ett shiftId, behåll den (t.ex. äldre ansökningar)
        if (!app.shiftId) return true;
        // Kolla om shiftId fortfarande finns
        return existingShiftIds.has(app.shiftId);
      });
  }, [applications, employeeNameMap, emailToEmployeeIdMap, existingShiftIds]);

  // Steg 1: Filtrera efter månad och år (datumfiltrering)
  const dateFilteredApplications = useMemo(() => {
    let filtered = enrichedApplications;

    if (selectedMonth && selectedYear) {
      const monthIndex = monthsInSwedish.indexOf(selectedMonth);
      const yearNum = parseInt(selectedYear, 10);

      filtered = filtered.filter((app) => {
        const appDate = app.date;
        if (!appDate) return false;
        return appDate.getMonth() === monthIndex && appDate.getFullYear() === yearNum;
      });
    }

    return filtered;
  }, [enrichedApplications, selectedMonth, selectedYear, monthsInSwedish]);

  // Steg 2: Filtrera efter status och sökning (för visning i tabellen)
  const filteredApplications = useMemo(() => {
    let filtered = dateFilteredApplications;

    if (selectedStatus !== "all") {
      filtered = filtered.filter((app) => app.status === selectedStatus);
    }

    if (searchQuery.trim()) {
      const queryLower = searchQuery.toLowerCase();
      filtered = filtered.filter((app) =>
        app.employeeName.toLowerCase().includes(queryLower)
      );
    }

    return filtered;
  }, [dateFilteredApplications, selectedStatus, searchQuery]);

  // Summering baserad på datumfiltrerade ansökningar (inte alla)
  const summary = useMemo(() => {
    const pending = dateFilteredApplications.filter((a) => a.status === "pending").length;
    const approved = dateFilteredApplications.filter((a) => a.status === "approved").length;
    const rejected = dateFilteredApplications.filter((a) => a.status === "rejected").length;
    const totalApprovedHours = dateFilteredApplications
      .filter((a) => a.status === "approved")
      .reduce((sum, a) => sum + (Number(a.hours) || 0), 0);

    return { pending, approved, rejected, totalApprovedHours };
  }, [dateFilteredApplications]);

  // Gruppera ansökningar per anställd (baserat på filtrerade ansökningar)
  const applicationsByEmployee = useMemo(() => {
    const grouped = new Map();

    filteredApplications.forEach(app => {
      const employeeName = app.employeeName;
      
      if (!grouped.has(employeeName)) {
        grouped.set(employeeName, {
          employeeName: employeeName,
          applications: [],
          totalHours: 0,
          approvedHours: 0,
          pendingCount: 0,
          approvedCount: 0,
          rejectedCount: 0,
        });
      }

      const group = grouped.get(employeeName);
      group.applications.push(app);
      group.totalHours += Number(app.hours) || 0;
      
      if (app.status === "approved") {
        group.approvedHours += Number(app.hours) || 0;
        group.approvedCount++;
      } else if (app.status === "pending") {
        group.pendingCount++;
      } else if (app.status === "rejected") {
        group.rejectedCount++;
      }
    });

    return Array.from(grouped.values()).sort((a, b) => a.employeeName.localeCompare(b.employeeName, "sv-SE"));
  }, [filteredApplications]);

  // Hjälpfunktion för att extrahera år och månad från application.date
  const getYearAndMonthFromApplication = (application) => {
    const appDate = application.date;
    if (!appDate) return { year: null, month: null };
    
    const date = appDate instanceof Date ? appDate : new Date(appDate);
    if (isNaN(date.getTime())) return { year: null, month: null };
    
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1
    };
  };

  // Godkänn ansökan
  const handleApprove = async (application, comment) => {
    if (!companyAccountUid) return;
    
    setProcessingId(application.id);
    try {
      let employeeId = application.employeeId;
      if (!employeeId && application.userEmail) {
        employeeId = emailToEmployeeIdMap[application.userEmail] || null;
      }
      
      const { year, month } = getYearAndMonthFromApplication(application);
      
      console.log(`Godkänner övertid för ${application.userEmail} med employeeId: ${employeeId}, år: ${year}, månad: ${month}`);
      
      const appRef = doc(db, "users", companyAccountUid, "overtimeRequests", application.id);
      
      const updateData = {
        status: "approved",
        reviewedBy: currentUser?.uid || null,
        reviewedAt: new Date(),
        reviewComment: comment || null,
        employeeId: employeeId,
      };
      
      if (year !== null && month !== null) {
        updateData.year = year;
        updateData.month = month;
      }
      
      await updateDoc(appRef, updateData);
      
      setShowModal(false);
      setSelectedApplication(null);
      setModalAction(null);
    } catch (error) {
      console.error("Fel vid godkännande:", error);
      alert("Kunde inte godkänna ansökan");
    } finally {
      setProcessingId(null);
    }
  };

  // Neka ansökan
  const handleReject = async (application, comment) => {
    if (!companyAccountUid) return;
    
    setProcessingId(application.id);
    try {
      let employeeId = application.employeeId;
      if (!employeeId && application.userEmail) {
        employeeId = emailToEmployeeIdMap[application.userEmail] || null;
      }
      
      const { year, month } = getYearAndMonthFromApplication(application);
      
      const appRef = doc(db, "users", companyAccountUid, "overtimeRequests", application.id);
      
      const updateData = {
        status: "rejected",
        reviewedBy: currentUser?.uid || null,
        reviewedAt: new Date(),
        reviewComment: comment || "Ansökan nekad",
        employeeId: employeeId,
      };
      
      if (year !== null && month !== null) {
        updateData.year = year;
        updateData.month = month;
      }
      
      await updateDoc(appRef, updateData);
      
      setShowModal(false);
      setSelectedApplication(null);
      setModalAction(null);
    } catch (error) {
      console.error("Fel vid nekande:", error);
      alert("Kunde inte neka ansökan");
    } finally {
      setProcessingId(null);
    }
  };

  // Återkalla ansökan
  const handleRevoke = async (application) => {
    if (!companyAccountUid) return;
    
    setProcessingId(application.id);
    try {
      const appRef = doc(db, "users", companyAccountUid, "overtimeRequests", application.id);
      
      await updateDoc(appRef, {
        status: "pending",
        reviewedBy: null,
        reviewedAt: null,
        reviewComment: null,
      });
      
      setShowModal(false);
      setSelectedApplication(null);
      setModalAction(null);
    } catch (error) {
      console.error("Fel vid återkallning:", error);
      alert("Kunde inte återkalla ansökan");
    } finally {
      setProcessingId(null);
    }
  };

  // Öppna modal
  const openModal = (application, action) => {
    setSelectedApplication(application);
    setModalAction(action);
    setShowModal(true);
  };

  const toggleExpand = (employeeName) => {
    setExpandedId(expandedId === employeeName ? null : employeeName);
  };

  // Stilar
  const styles = {
    page: {
      minHeight: "100vh",
      background: "#f6f7fb",
      padding: "22px 24px 40px",
      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
      color: "#1f2937",
    },
    container: {
      maxWidth: "1400px",
      margin: "0 auto",
    },
    header: {
      marginBottom: "24px",
    },
    title: {
      fontSize: "32px",
      fontWeight: 800,
      color: "#101b3a",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "8px",
    },
    titleIcon: {
      background: "linear-gradient(135deg, #f59e0b, #f97316)",
      padding: "10px",
      borderRadius: "14px",
      color: "#fff",
    },
    subtitle: {
      color: "#6b7280",
      fontSize: "14px",
    },

    summaryCards: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "16px",
      marginBottom: "24px",
    },
    summaryCard: {
      background: "#fff",
      borderRadius: "12px",
      padding: "16px 20px",
      border: "1px solid #e5e7eb",
    },
    summaryCardTitle: {
      fontSize: "13px",
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      marginBottom: "8px",
    },
    summaryCardValue: {
      fontSize: "28px",
      fontWeight: 800,
      color: "#111827",
    },

    filtersBar: {
      background: "#fff",
      borderRadius: "12px",
      padding: "16px",
      marginBottom: "20px",
      border: "1px solid #e5e7eb",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },

    searchBox: {
      width: "300px",
      position: "relative",
      minWidth: "150px",
      flexShrink: 0,
    },
    searchIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
    },
    searchInput: {
      width: "100%",
      height: "40px",
      boxSizing: "border-box",
      padding: "0 12px 0 36px",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      fontSize: "14px",
      outline: "none",
    },

    select: {
      height: "40px",
      padding: "0 36px 0 12px",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      fontSize: "14px",
      backgroundColor: "#fff",
      color: "#374151",
      cursor: "pointer",
      outline: "none",
      minWidth: "120px",
      flexShrink: 0,
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      backgroundImage:
        "linear-gradient(45deg, transparent 50%, #6b7280 50%), linear-gradient(135deg, #6b7280 50%, transparent 50%)",
      backgroundPosition: "calc(100% - 16px) 17px, calc(100% - 11px) 17px",
      backgroundSize: "5px 5px, 5px 5px",
      backgroundRepeat: "no-repeat",
    },

    statusFilter: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexWrap: "wrap",
      marginLeft: "auto",
      justifyContent: "flex-end",
    },
    statusBtn: {
      height: "40px",
      padding: "0 16px",
      borderRadius: "20px",
      fontSize: "13px",
      fontWeight: 500,
      border: "1px solid #e5e7eb",
      background: "#fff",
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    statusBtnActive: {
      background: "#f59e0b",
      borderColor: "#f59e0b",
      color: "#fff",
    },

    table: {
      background: "#fff",
      borderRadius: "12px",
      border: "1px solid #e5e7eb",
      overflow: "hidden",
    },
    tableHeader: {
      background: "#f9fafb",
      padding: "14px 16px",
      borderBottom: "1px solid #e5e7eb",
      display: "grid",
      gridTemplateColumns: "200px 100px 100px 120px",
      gap: "12px",
      fontWeight: 600,
      fontSize: "13px",
      color: "#6b7280",
    },
    employeeRow: {
      cursor: "pointer",
      transition: "background 0.2s",
    },
    tableRow: {
      padding: "14px 16px",
      borderBottom: "1px solid #e5e7eb",
      display: "grid",
      gridTemplateColumns: "200px 100px 100px 120px",
      gap: "12px",
      alignItems: "center",
    },
    employeeName: {
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 10px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: 500,
      width: "fit-content",
    },

    detailsRow: {
      background: "#fafcff",
    },
    detailsCell: {
      padding: "20px 24px",
      borderBottom: "1px solid #edf2f7",
    },
    appCard: {
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "10px",
      padding: "14px",
      marginBottom: "12px",
    },
    appHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "10px",
      flexWrap: "wrap",
      gap: "8px",
    },
    appDate: {
      fontWeight: 700,
      color: "#1f2937",
    },
    appActions: {
      display: "flex",
      gap: "8px",
      marginLeft: "auto",
    },
    actionBtn: {
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "12px",
      fontWeight: 500,
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
    },
    btnApprove: {
      backgroundColor: "#10b981",
      color: "#fff",
    },
    btnReject: {
      backgroundColor: "#ef4444",
      color: "#fff",
    },
    btnRevoke: {
      backgroundColor: "#f59e0b",
      color: "#fff",
    },

    appDetails: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginTop: "8px",
      fontSize: "13px",
      color: "#4b5563",
    },
    detailRow: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexWrap: "wrap",
    },
    shiftContainer: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      backgroundColor: "#F3F4F6",
      padding: "4px 8px",
      borderRadius: "8px",
      alignSelf: "flex-start",
    },
    emptyState: {
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "48px",
      textAlign: "center",
      color: "#9ca3af",
    },
  };

  // Visa laddningsindikator medan företag laddas
  if (companyLoading) {
    return <LoadingIndicator message="Laddar företagsinformation..." fullPage />;
  }

  // Visa meddelande om inget företag är valt
  if (!activeCompany) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.emptyState}>
            <FaBusinessTime size={48} style={{ marginBottom: "16px", opacity: 0.5 }} />
            <div>Inget företag valt. Vänligen logga in igen.</div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingIndicator message="Laddar övertidsansökningar..." fullPage />;
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header med företagsnamn */}
        <div style={styles.header}>
          <h1 style={styles.title}>
            <span style={styles.titleIcon}>
              <FaBusinessTime size={24} />
            </span>
            Övertidsansökningar
          </h1>
          <p style={styles.subtitle}>
            Hantera inkomna övertidsansökningar för <strong>{activeCompany?.companyName}</strong>
          </p>
        </div>

        {/* Summary Cards - Visar statistik för vald månad/år */}
        <div style={styles.summaryCards}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryCardTitle}>Väntar</div>
            <div style={{ ...styles.summaryCardValue, color: "#f59e0b" }}>{summary.pending}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryCardTitle}>Godkända</div>
            <div style={{ ...styles.summaryCardValue, color: "#10b981" }}>{summary.approved}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryCardTitle}>Nekade</div>
            <div style={{ ...styles.summaryCardValue, color: "#ef4444" }}>{summary.rejected}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryCardTitle}>Godkända timmar</div>
            <div style={styles.summaryCardValue}>{formatHours(summary.totalApprovedHours)} h</div>
          </div>
        </div>

        {/* Filters */}
        <div style={styles.filtersBar}>
          <div style={styles.searchBox}>
            <FaSearch style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Sök efter anställd..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          <select
            style={styles.select}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {monthsInSwedish.map((month) => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>

          <select
            style={styles.select}
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          <div style={styles.statusFilter}>
            <button
              style={{ ...styles.statusBtn, ...(selectedStatus === "pending" && styles.statusBtnActive) }}
              onClick={() => setSelectedStatus("pending")}
            >
              Väntar ({summary.pending})
            </button>
            <button
              style={{ ...styles.statusBtn, ...(selectedStatus === "approved" && styles.statusBtnActive) }}
              onClick={() => setSelectedStatus("approved")}
            >
              Godkända ({summary.approved})
            </button>
            <button
              style={{ ...styles.statusBtn, ...(selectedStatus === "rejected" && styles.statusBtnActive) }}
              onClick={() => setSelectedStatus("rejected")}
            >
              Nekade ({summary.rejected})
            </button>
          </div>
        </div>

        {/* Applications Table */}
        {applicationsByEmployee.length === 0 ? (
          <div style={styles.emptyState}>
            <FaRegClock size={48} style={{ marginBottom: "16px", opacity: 0.5 }} />
            <div>Inga övertidsansökningar för vald period</div>
          </div>
        ) : (
          <div style={styles.table}>
            <div style={styles.tableHeader}>
              <div>Anställd</div>
              <div>Totalt timmar</div>
              <div>Godkända timmar</div>
              <div>Status</div>
            </div>

            {applicationsByEmployee.map((employee) => (
              <React.Fragment key={employee.employeeName}>
                <div
                  style={styles.employeeRow}
                  onClick={() => toggleExpand(employee.employeeName)}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fc"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <div style={styles.tableRow}>
                    <div style={styles.employeeName}>
                      <FaUser size={14} color="#6b7280" />
                      {employee.employeeName}
                    </div>
                    <div>{formatHours(employee.totalHours)} h</div>
                    <div style={{ color: "#10b981", fontWeight: 600 }}>{formatHours(employee.approvedHours)} h</div>
                    <div>
                      {employee.pendingCount > 0 && (
                        <span style={{ ...styles.statusBadge, background: "#FEF3C7", color: "#F59E0B", marginRight: "6px" }}>
                          <FaHourglassHalf size={10} /> {employee.pendingCount}
                        </span>
                      )}
                      {employee.approvedCount > 0 && (
                        <span style={{ ...styles.statusBadge, background: "#D1FAE5", color: "#10B981", marginRight: "6px" }}>
                          <FaCheckCircle size={10} /> {employee.approvedCount}
                        </span>
                      )}
                      {employee.rejectedCount > 0 && (
                        <span style={{ ...styles.statusBadge, background: "#FEE2E2", color: "#EF4444" }}>
                          <FaTimesCircle size={10} /> {employee.rejectedCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {expandedId === employee.employeeName && (
                  <div style={styles.detailsRow}>
                    <div style={styles.detailsCell}>
                      <strong style={{ display: "block", marginBottom: "12px" }}>
                        📋 Ansökningar för {employee.employeeName}:
                      </strong>
                      {employee.applications.map((app) => {
                        const isApproved = app.status === "approved";
                        const isRejected = app.status === "rejected";
                        
                        return (
                          <div key={app.id} style={styles.appCard}>
                            <div style={styles.appHeader}>
                              <span style={styles.appDate}>📅 {formatDate(app.date)}</span>
                              <div style={styles.appActions}>
                                {app.status === "pending" && (
                                  <>
                                    <button
                                      style={{ ...styles.actionBtn, ...styles.btnApprove }}
                                      onClick={() => openModal(app, "approve")}
                                    >
                                      <FaCheck size={12} /> Godkänn
                                    </button>
                                    <button
                                      style={{ ...styles.actionBtn, ...styles.btnReject }}
                                      onClick={() => openModal(app, "reject")}
                                    >
                                      <FaTimes size={12} /> Neka
                                    </button>
                                  </>
                                )}
                                {(isApproved || isRejected) && (
                                  <button
                                    style={{ ...styles.actionBtn, ...styles.btnRevoke }}
                                    onClick={() => openModal(app, "revoke")}
                                  >
                                    <FaUndo size={12} /> Återkalla
                                  </button>
                                )}
                              </div>
                            </div>
                            <div style={styles.appDetails}>
                              <div style={styles.detailRow}>
                                <span>⏰ Tid: {formatTime(app.startTime)} - {formatTime(app.endTime)}</span>
                              </div>
                              
                              {app.shiftTitle && (
                                <div style={styles.shiftContainer}>
                                  <FaBriefcase size={12} color="#6B7280" />
                                  <span>Pass: {app.shiftTitle}</span>
                                </div>
                              )}

                              {app.shiftStartTime && app.shiftEndTime && (
                                <div style={styles.shiftContainer}>
                                  <FaRegClock size={12} color="#6B7280" />
                                  <span>Ordinarie tid: {formatTime(app.shiftStartTime)} - {formatTime(app.shiftEndTime)}</span>
                                </div>
                              )}

                              <div style={styles.detailRow}>
                                <span>⏱️ Övertidstimmar: {formatHours(app.hours)} h</span>
                              </div>
                              <div style={styles.detailRow}>
                                <span>📨 Insänd: {formatDateTime(app.submittedAt)}</span>
                              </div>
                              {app.reason && (
                                <div style={styles.detailRow}>
                                  <span>💬 Anledning: {app.reason}</span>
                                </div>
                              )}
                              {app.reviewComment && (
                                <div style={styles.detailRow}>
                                  <span>💭 Kommentar: {app.reviewComment}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showModal && selectedApplication && (
        <ReviewModal
          application={selectedApplication}
          actionType={modalAction}
          onClose={() => {
            setShowModal(false);
            setSelectedApplication(null);
            setModalAction(null);
          }}
          onApprove={handleApprove}
          onReject={handleReject}
          onRevoke={handleRevoke}
          isProcessing={processingId === selectedApplication.id}
        />
      )}
    </div>
  );
}
